/**
 * React Query hooks for library review workflow
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useRef } from 'react';
import { generateLibrarySuggestions } from '@/api/libraries';
import { getEntries, createMissingEntry } from '@/api/entries';
import {
  LibrarySuggestionRequest,
  LibrarySuggestionResponse,
  VerifiedEntry,
  CreateMissingEntryRequest,
  EntrySuggestion,
} from '@/types/api';
import { getErrorMessage, getToken } from '@/api/client';

export const useGenerateLibrarySuggestions = (libraryId: number) => {
  const queryClient = useQueryClient();

  return useMutation<LibrarySuggestionResponse, Error, LibrarySuggestionRequest>({
    mutationFn: (data) => generateLibrarySuggestions(libraryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
    onError: (error) => {
      console.error('Failed to generate library suggestions:', getErrorMessage(error));
    },
  });
};

export const useLibraryEntries = (libraryId: number) => {
  return useQuery<VerifiedEntry[], Error>({
    queryKey: ['libraries', libraryId, 'entries'],
    queryFn: () => getEntries({ library_id: libraryId, limit: 1000 }),
    enabled: !!libraryId,
  });
};

export const useCreateMissingEntry = () => {
  const queryClient = useQueryClient();

  return useMutation<VerifiedEntry, Error, CreateMissingEntryRequest>({
    mutationFn: createMissingEntry,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      queryClient.invalidateQueries({ queryKey: ['libraries', variables.library_id, 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['libraries', variables.library_id, 'stats'] });
    },
    onError: (error) => {
      console.error('Failed to create missing entry:', getErrorMessage(error));
    },
  });
};

// ============================================================================
// Streaming Suggestions Hook
// ============================================================================

interface StreamProgress {
  processed: number;
  total: number;
  percentage: number;
  message?: string;
  section_id?: number;
}

interface StreamStats {
  total_suggestions: number;
  existing_entries: number;
  new_suggestions: number;
  avg_confidence: number;
}

interface StreamingState {
  isStreaming: boolean;
  progress: StreamProgress | null;
  suggestions: EntrySuggestion[];
  stats: StreamStats | null;
  error: string | null;
  sectionsWithErrors: Array<{ section_id: number; error: string }>;
}

export const useStreamLibrarySuggestions = (libraryId: number) => {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);

  const [state, setState] = useState<StreamingState>({
    isStreaming: false,
    progress: null,
    suggestions: [],
    stats: null,
    error: null,
    sectionsWithErrors: [],
  });

  const startStreaming = useCallback(
    async (request: LibrarySuggestionRequest) => {
      // Reset state
      setState({
        isStreaming: true,
        progress: null,
        suggestions: [],
        stats: null,
        error: null,
        sectionsWithErrors: [],
      });

      try {
        // Build query parameters
        const params = new URLSearchParams({
          min_confidence: (request.min_confidence ?? 0.5).toString(),
          include_existing: (request.include_existing ?? false).toString(),
        });

        if (request.section_ids && request.section_ids.length > 0) {
          params.append('section_ids', request.section_ids.join(','));
        }

        // Create EventSource. The token rides in the query string because
        // EventSource cannot set an Authorization header (same pattern as
        // api/suggestions.ts); the backend rejects the stream without it.
        const token = getToken();
        const url = `/api/v1/libraries/${libraryId}/suggestions/stream?${params}${token ? `&token=${token}` : ''}`;
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;

        // Handle progress events
        eventSource.addEventListener('progress', (e) => {
          const data = JSON.parse(e.data) as StreamProgress;
          setState((prev) => ({ ...prev, progress: data }));
        });

        // Handle section completion events
        eventSource.addEventListener('section_complete', (e) => {
          const data = JSON.parse(e.data);
          console.log('Section complete:', data);
        });

        // Handle suggestions events
        eventSource.addEventListener('suggestions', (e) => {
          const data = JSON.parse(e.data);
          const newSuggestions = data.suggestions as EntrySuggestion[];
          setState((prev) => ({
            ...prev,
            suggestions: [...prev.suggestions, ...newSuggestions],
          }));
        });

        // Handle error events
        eventSource.addEventListener('error', (e) => {
          const errorEvent = e as MessageEvent;
          if (errorEvent.data) {
            const data = JSON.parse(errorEvent.data);
            setState((prev) => ({
              ...prev,
              error: data.message || 'Unknown error',
              sectionsWithErrors: data.section_id
                ? [...prev.sectionsWithErrors, { section_id: data.section_id, error: data.message }]
                : prev.sectionsWithErrors,
            }));
          }
        });

        // Handle completion events
        eventSource.addEventListener('complete', (e) => {
          const data = JSON.parse(e.data);
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            stats: {
              total_suggestions: data.total_suggestions || 0,
              existing_entries: data.existing_entries || 0,
              new_suggestions: data.new_suggestions || 0,
              avg_confidence: data.avg_confidence || 0,
            },
          }));

          // Close EventSource
          eventSource.close();
          eventSourceRef.current = null;

          // Invalidate queries
          queryClient.invalidateQueries({ queryKey: ['entries'] });
        });

        // Handle connection errors
        eventSource.onerror = () => {
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error: prev.error || 'Connection error occurred',
          }));
          eventSource.close();
          eventSourceRef.current = null;
        };
      } catch (error) {
        console.error('Failed to start streaming:', error);
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: getErrorMessage(error),
        }));
      }
    },
    [libraryId, queryClient]
  );

  const stopStreaming = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      isStreaming: false,
    }));
  }, []);

  return {
    ...state,
    startStreaming,
    stopStreaming,
  };
};
